import { Fragment } from "react";
import { CalendarDays } from "lucide-react";
import type { HeatmapDayUI } from "../../types";

interface DailyHeatmapProps {
  heatmap: HeatmapDayUI[];
  month: number; // 0-based
  year: number;
}

const INTENSITY_COLORS = [
  "var(--surface-muted)",
  "var(--heatmap-1)",
  "var(--heatmap-2)",
  "var(--heatmap-3)",
  "var(--heatmap-4)",
];
const DOWS = ["S", "M", "T", "W", "T", "F", "S"];

function formatAmount(n: number): string {
  if (!n) return "";
  if (n >= 1000) return `${Math.round(n / 100) / 10}k`;
  return String(n);
}

export default function DailyHeatmap({
  heatmap,
  month,
  year,
}: DailyHeatmapProps) {
  // Get day-of-week the 1st falls on (0=Sun)
  const startDow = new Date(year, month, 1).getDay();
  const daysInMonth = heatmap.length;

  // Build a grid: 5-6 weeks × 7 cols
  const cells: (HeatmapDayUI | null)[] = [
    ...Array<null>(startDow).fill(null),
    ...heatmap,
  ];
  // Pad to complete last week
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks = Math.ceil(cells.length / 7);

  return (
    <div className="rounded-[14px] px-5 py-5 sm:px-6 bg-card border-[0.5px] border-border shadow-card">
      {/* Header */}
      <div className="flex items-center justify-between mb-3.5">
        <h2 className="flex items-center gap-2 text-[15px] font-medium m-0 text-foreground">
          <CalendarDays className="w-4 h-4 text-[var(--text-secondary)]" />
          Daily heat ·{" "}
          {
            [
              "Jan",
              "Feb",
              "Mar",
              "Apr",
              "May",
              "Jun",
              "Jul",
              "Aug",
              "Sep",
              "Oct",
              "Nov",
              "Dec",
            ][month]
          }
        </h2>
        <span className="hidden sm:inline text-[11px] tracking-[0.04em] text-muted-foreground">
          darker = more spent
        </span>
      </div>

      {/* Grid: week-label col + 7 day cols */}
      <div
        className="grid gap-1 grid-cols-7 sm:gap-1.5 sm:grid-cols-[28px_repeat(7,1fr)]"
        id="heatmap"
        aria-label="Calendar heatmap"
      >
        {/* DOW headers */}
        <div className="hidden sm:block" />
        {DOWS.map((d, i) => (
          <div
            key={i}
            className="text-center text-[10px] tracking-[0.08em] text-muted-foreground"
          >
            {d}
          </div>
        ))}

        {/* Weeks */}
        {Array.from({ length: weeks }, (_, wi) => {
          const weekCells = cells.slice(wi * 7, wi * 7 + 7);
          return (
            <Fragment key={wi}>
              {/* Week label */}
              <div
                key={`wl-${wi}`}
                className="hidden sm:flex text-right text-[10px] pr-1 items-center justify-end leading-8 text-muted-foreground"
              >
                w{wi + 1}
              </div>
              {weekCells.map((day, di) => {
                if (!day) {
                  return (
                    <div
                      key={`empty-${wi}-${di}`}
                      className="h-11 sm:h-[52px] rounded-lg bg-[var(--surface-muted)] opacity-25"
                    />
                  );
                }
                const textClass =
                  day.intensity >= 3
                    ? "text-[var(--heatmap-text-hi)]"
                    : day.intensity >= 1
                      ? "text-[var(--heatmap-text-mid)]"
                      : "text-muted-foreground";
                return (
                  <div
                    key={day.date}
                    className={`rounded-lg relative flex flex-col items-center justify-between px-0.5 py-1.5 h-11 sm:h-[52px] font-medium transition-transform hover:scale-105 cursor-default ${textClass} ${day.isFuture ? "opacity-50" : "opacity-100"}`}
                    data-level={day.intensity}
                    title={`${day.dayOfMonth} · ฿${day.amount.toLocaleString("en-US")}`}
                    style={{
                      background: INTENSITY_COLORS[day.intensity],
                      outline: day.isToday
                        ? "2px solid var(--text-primary)"
                        : undefined,
                      outlineOffset: day.isToday ? "1px" : undefined,
                    }}
                  >
                    <span className="flex items-center gap-0.5 leading-none">
                      <span className="tabular-nums text-[10px]">
                        {day.dayOfMonth}
                      </span>
                    </span>
                    <span className="tabular-nums text-[9px] font-bold leading-none opacity-85 min-h-[10px]">
                      {!day.isFuture && day.amount > 0
                        ? formatAmount(day.amount)
                        : ""}
                    </span>
                  </div>
                );
              })}
            </Fragment>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-end gap-1.5 mt-3.5 text-[10px] tracking-[0.04em] text-muted-foreground">
        <span>less</span>
        <div className="flex gap-0.5">
          {INTENSITY_COLORS.map((bg, i) => (
            <span
              key={i}
              className="w-3 h-3 rounded-[3px] inline-block"
              style={{ background: bg }}
            />
          ))}
        </div>
        <span>more</span>
      </div>

      <p className="sr-only">
        Days in month: {daysInMonth}. Each cell shows spending amount for that
        day.
      </p>
    </div>
  );
}
